/**
 * use-platform-release-notes.ts — Hooks cho Platform → Thông báo cập nhật (super-admin).
 * Backend: ecdp_backend_source/src/modules/platform/release-notes-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'

export interface PlatformReleaseNote {
  id: string
  slug: string
  title: string
  releasedAt: string
  highlights: string[]
  html: string
  maxShows: number
  isActive: boolean
  updatedAt: string
}
export type ReleaseNoteInput = Omit<PlatformReleaseNote, 'id' | 'updatedAt'>

const KEY = ['platform', 'release-notes'] as const

export function usePlatformReleaseNotes() {
  return useQuery<PlatformReleaseNote[]>({
    queryKey: KEY,
    queryFn: async () => (await platformApi.get<{ releaseNotes: PlatformReleaseNote[] }>('/platform/release-notes')).data.releaseNotes,
  })
}

export function useSaveReleaseNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ReleaseNoteInput }) =>
      (id ? await platformApi.put<PlatformReleaseNote>(`/platform/release-notes/${id}`, input) : await platformApi.post<PlatformReleaseNote>('/platform/release-notes', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useToggleReleaseNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: { id: string; isActive: boolean }) => (await platformApi.patch<PlatformReleaseNote>(`/platform/release-notes/${v.id}`, { isActive: v.isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteReleaseNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await platformApi.delete(`/platform/release-notes/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
